// Subscription page — displays billing status, IdP provider, and tier entitlements.
// Admin-only (route-level guard not needed — sidebar only shows for admins,
// and backend enforces requireTenantAdmin).

import { useTranslation } from 'react-i18next'
import { useQuery } from '@tanstack/react-query'
import { useApi, ApiRequestError } from '@/lib/api/client'
import { useTenant } from '@/components/shared/TenantShell'
import { platformApi } from '@/lib/api/platform'
import { queryKeys } from '@/lib/api/queryKeys'
import { ErrorState } from '@/components/shared/ErrorState'
import { FormSkeleton } from '@/components/shared/LoadingSkeleton'
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/card'
import { BillingStatusCard } from '@/pages/platform/BillingStatusCard'

/** Maps entitlement keys to human-readable labels. */
const ENTITLEMENT_DISPLAY_ORDER = [
  'register_count',
  'register_hierarchy_depth',
  'category_hierarchy_depth',
  'taxonomy_depth',
  'impact_tables',
  'risk_appetite_granularity',
  'parent_child_risks',
  'confirmation_gates',
  'secondary_signoff',
  'assessment_overdue_notifications',
  'compare_date_reporting',
  'genai_api_available',
  'genai_api_keys',
]

function formatEntitlementValue(key: string, value: unknown, t: (k: string) => string): string {
  if (typeof value === 'boolean') {
    return value
      ? t('platform:subscription.entitlementValues.included')
      : t('platform:subscription.entitlementValues.notIncluded')
  }
  if (typeof value === 'number') {
    if (value === -1) return t('platform:subscription.entitlementValues.unlimited')
    if (value === 0 && (key.includes('depth') || key.includes('hierarchy'))) {
      return t('platform:subscription.entitlementValues.flat')
    }
    return String(value)
  }
  if (typeof value === 'string') {
    // Granularity values
    const granularityKey = `platform:subscription.entitlementValues.${value}`
    const translated = t(granularityKey)
    return translated !== granularityKey ? translated : value
  }
  return String(value)
}


export function SubscriptionPage() {
  const { t } = useTranslation(['platform', 'common'])
  const api = useApi()
  const { slug } = useTenant()
  const pApi = platformApi(api)

  const {
    data: subscription,
    error,
    isLoading,
    refetch,
  } = useQuery({
    queryKey: queryKeys.platform.subscription(slug),
    queryFn: () => pApi.getSubscription(),
    retry: false,
  })

  if (isLoading) return <FormSkeleton />

  if (error) {
    return (
      <ErrorState
        message={t('common:errors.loadFailed', { resource: t('subscription.title') })}
        technicalDetail={error instanceof ApiRequestError ? error.message : undefined}
        onRetry={() => refetch()}
      />
    )
  }

  if (!subscription) return null

  return (
    <div className="max-w-2xl space-y-6">
      <div>
        <h1 className="text-2xl font-bold">{t('subscription.title')}</h1>
        <p className="text-muted-foreground">{t('subscription.description')}</p>
      </div>

      {/* Billing status card */}
      <BillingStatusCard info={subscription} />

      {/* Identity Provider */}
      <Card>
        <CardHeader>
          <CardTitle>{t('subscription.idpProvider')}</CardTitle>
        </CardHeader>
        <CardContent>
          {subscription.idp_provider_name ? (
            <p className="text-sm">{subscription.idp_provider_name}</p>
          ) : (
            <p className="text-sm text-muted-foreground">{t('subscription.idpNotConfigured')}</p>
          )}
        </CardContent>
      </Card>

      {/* Entitlements */}
      <Card>
        <CardHeader>
          <CardTitle>{t('subscription.entitlements')}</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-2">
            {ENTITLEMENT_DISPLAY_ORDER.map((key) => {
              const value = subscription.entitlements[key]
              if (value === undefined) return null
              const label = t(`subscription.entitlementLabels.${key}`)
              return (
                <div key={key} className="flex items-center justify-between py-1 text-sm">
                  <span>{label}</span>
                  <span className="font-medium">{formatEntitlementValue(key, value, t)}</span>
                </div>
              )
            })}
          </div>
        </CardContent>
      </Card>
    </div>
  )
}
